import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { NOTIFY_ME_SNACK_BAR, NOTIFY_ME_MODAL_VIEWS } from "rtr-constants";
import { MODAL_NAMES } from "constants/modal-names";
import { differenceInDays } from "date-fns";
import $ from "clients/RawClient";
import {
  updateNotifyMeLocalStorage,
  getNotifyMeLocalStorage,
  formatDiscoServedNotifyMeResponse,
} from "helpers/notify-me-helpers";
import HeapHelpers, { HEAP_NOTIFY_ME_EVENTS } from "helpers/heap-helpers";

const API_PATH = "/api/notify-me";

export const initialState = {
  cameFromManageNotificationsView: false,
  notifyMeError: null,
  notifyMeFetched: false,
  notifyMeLoading: false,
  notifyMeProducts: null,
  notifyMeView: NOTIFY_ME_MODAL_VIEWS.FORM,
  pdpSelectedSize: null,
};

function createSnackBarObject(snackBarProps) {
  const snackBarObject = {
    ...snackBarProps,
    isVisible: true,
  };
  return snackBarObject;
}

export const fetchNotifyMeProducts = createAsyncThunk("notifyMe/fetchProducts", ({ userId }) => {
  const lsParsedObject = getNotifyMeLocalStorage();

  // if we have a recently updated localStorage object, return that instead without resetting the timestamp
  if (lsParsedObject?.timestamp && differenceInDays(Date.now(), lsParsedObject.timestamp) < 1) {
    return new Promise(resolve => resolve(lsParsedObject.existingNotifyMeProducts));
  }

  // otherwise, update localstorage as soon as we get a successful response and update the timestamp
  return $.ajax({
    type: "GET",
    url: `${API_PATH}?userId=${userId}`,
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json; charset=utf-8",
    },
  }).then(res => {
    updateNotifyMeLocalStorage(res);
    return res;
  });
});

export const postNotifyMeProduct = createAsyncThunk("notifyMe/postProduct", ({ userId, style, skus }) =>
  $.ajax({
    type: "POST",
    url: API_PATH,
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json; charset=utf-8",
    },
    data: JSON.stringify({ userId, style, skus }),
  })
);

export const updateNotifyMeProduct = createAsyncThunk(
  "notifyMe/updateProduct",
  ({ userId, style, skus, cameFromManageNotificationsView }) =>
    $.ajax({
      type: "PATCH",
      url: API_PATH,
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json; charset=utf-8",
      },
      data: JSON.stringify({ userId, style, skus, includeProductDetails: cameFromManageNotificationsView }),
    })
);

const notifyMeSlice = createSlice({
  name: "notifyMe",
  initialState,
  reducers: {
    changeNotifyMeModalView: (state, action) => {
      state.notifyMeView = action.payload.view;
      if (action.payload.snackBarProps) {
        state.snackBar = createSnackBarObject(action.payload.snackBarProps);
      }
    },
    closeNotifyMeModal: (state, action) => {
      if (action.payload?.toast) {
        state.snackBar = createSnackBarObject(action.payload.toast);
      }
      state.pdpSelectedSize = null;
      state.displayedModal = null;
      state.notifyMeView = NOTIFY_ME_MODAL_VIEWS.FORM;
      state.cameFromManageNotificationsView = false;
    },
    // manage notifications modal
    editNotificationFromManageNotifications: (state, action) => {
      // !!!!!!!!!!!!! COULD HAVE A HUGE SIDE EFFECT !!!!!!!!!!!!!
      // because we are updating product, this could potentially cause changes with any component
      // listening to products. Because this function for now is only being called from user/notifications
      // where no products are being read except within manage notifications modal, we should be ok
      // AV 05/06/25
      state.product = action.payload;
      state.notifyMeView = NOTIFY_ME_MODAL_VIEWS.FORM;
      state.cameFromManageNotificationsView = true;
    },
    openManageNotificationsModal: (state, action) => {
      state.notifyMeProducts = action.payload;
      state.displayedModal = MODAL_NAMES.NOTIFY_ME;
      state.notifyMeView = NOTIFY_ME_MODAL_VIEWS.MANAGE;
    },
    openNotifyMeModal: (state, action) => {
      if (state.gridProductDrawer) {
        state.gridProductDrawer = null;
      }
      state.pdpSelectedSize = action.payload;
      state.displayedModal = MODAL_NAMES.NOTIFY_ME;
      state.notifyMeView = NOTIFY_ME_MODAL_VIEWS.FORM;
      HeapHelpers.fireHeapEvent(HEAP_NOTIFY_ME_EVENTS.PDP_CTA, {});
    },
    setServerSideFetchedNotifyMe: (state, action) => {
      const notifyMeProducts = action.payload;

      updateNotifyMeLocalStorage(notifyMeProducts, true);
      state.notifyMeProducts = notifyMeProducts;
      state.notifyMeFetched = true;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchNotifyMeProducts.fulfilled, (state, action) => {
      state.notifyMeProducts = action.payload;
      state.notifyMeFetched = true;
    });
    builder.addCase(fetchNotifyMeProducts.rejected, state => {
      state.notifyMeProducts = null;
      state.notifyMeFetched = true;
    });
    builder.addCase(postNotifyMeProduct.fulfilled, (state, action) => {
      const { notifyMeItems, notificationRules } = action.payload;
      updateNotifyMeLocalStorage(notifyMeItems);
      state.notifyMeProducts = notifyMeItems;
      state.notificationRules = notificationRules;
      state.notifyMeView = NOTIFY_ME_MODAL_VIEWS.SUCCESS;
      state.notifyMeError = null;
      state.notifyMeLoading = false;
    });
    builder.addCase(postNotifyMeProduct.pending, state => {
      state.notifyMeLoading = true;
    });
    builder.addCase(postNotifyMeProduct.rejected, (state, action) => {
      state.notifyMeError = action.error?.message || NOTIFY_ME_SNACK_BAR.ERROR;
      state.notifyMeLoading = false;
    });
    builder.addCase(updateNotifyMeProduct.fulfilled, (state, action) => {
      const isDiscoServedProduct = state.cameFromManageNotificationsView;
      updateNotifyMeLocalStorage(action.payload, isDiscoServedProduct);
      const newNotifyMeProducts = isDiscoServedProduct
        ? formatDiscoServedNotifyMeResponse(action.payload)
        : action.payload;

      state.notifyMeProducts = newNotifyMeProducts;
      state.notifyMeError = null;
      state.notifyMeLoading = false;
    });
    builder.addCase(updateNotifyMeProduct.pending, state => {
      state.notifyMeLoading = true;
    });
    builder.addCase(updateNotifyMeProduct.rejected, (state, action) => {
      state.notifyMeError = action.error?.message || NOTIFY_ME_SNACK_BAR.ERROR;
      state.notifyMeLoading = false;
    });
  },
});

/**
 * This value isn't used anywhere.
 * It's only defined because ReduxApp expects reducers to have a "types" property
 */
export const types = [
  "notifyMe/changeNotifyMeModalView",
  "notifyMe/closeNotifyMeModal",
  "notifyMe/editNotificationFromManageNotifications",
  "notifyMe/fetchProducts/fulfilled",
  "notifyMe/fetchProducts/rejected",
  "notifyMe/openManageNotificationsModal",
  "notifyMe/openNotifyMeModal",
  "notifyMe/postProduct/fulfilled",
  "notifyMe/postProduct/pending",
  "notifyMe/postProduct/rejected",
  "notifyMe/setServerSideFetchedNotifyMe",
  "notifyMe/updateProduct/fulfilled",
  "notifyMe/updateProduct/pending",
  "notifyMe/updateProduct/rejected",
];

export const {
  changeNotifyMeModalView,
  closeNotifyMeModal,
  editNotificationFromManageNotifications,
  openManageNotificationsModal,
  openNotifyMeModal,
  setServerSideFetchedNotifyMe,
} = notifyMeSlice.actions;

export default {
  types: types,
  fn: notifyMeSlice.reducer,
};
